import { useEffect } from "react";

/**
 * A page's "unsaved changes" question, asked by a way out the page does not draw itself: the
 * layout's "← Back to Settings" (SettingsBackLink). The page registers what to ask while it is
 * mounted; the way out asks it and leaves only on yes. One page at a time (the routed page).
 *
 * A UI nicety, nothing more: it guards no data, and with nothing registered every way out leaves.
 */
type LeaveGuard = () => Promise<boolean>;

let current: LeaveGuard | null = null;

/** Registers `guard` while the calling page is mounted; null registers nothing. */
export function useLeaveGuard(guard: LeaveGuard | null): void {
  useEffect(() => {
    if (!guard) return undefined;
    current = guard;
    return () => {
      if (current === guard) current = null;
    };
  }, [guard]);
}

/**
 * Leaves through `leave` once the mounted page agrees: at once when no page asks anything (so a
 * plain way out stays synchronous), after the page's answer otherwise.
 */
export function leaveWhenAllowed(leave: () => void): void {
  if (!current) {
    leave();
    return;
  }
  void current().then((ok) => {
    if (ok) leave();
  });
}
